import { parseCookies } from 'nookies';

import { leapUrl } from './leapUrl';

/*
 * A sign-in attempt we triggered ourselves, remembered just long enough to notice that it
 * did not help. sessionStorage throws in some privacy modes, so every access is guarded and
 * the fallback is the old behaviour rather than a broken page.
 */
const AUTH_RETRY_KEY = 'linkpass_auth_retry_at';
const AUTH_RETRY_WINDOW_MS = 30000;

function recentlySentToLogin() {
  try {
    return (
      Date.now() - Number(window.sessionStorage.getItem(AUTH_RETRY_KEY) || 0) < AUTH_RETRY_WINDOW_MS
    );
  } catch (err) {
    return false;
  }
}

function markSentToLogin() {
  try {
    window.sessionStorage.setItem(AUTH_RETRY_KEY, String(Date.now()));
  } catch (err) {
    // nothing to do - without it we simply keep the pre-existing behaviour
  }
}

function clearSentToLogin() {
  try {
    window.sessionStorage.removeItem(AUTH_RETRY_KEY);
  } catch (err) {
    // as above
  }
}

/*
 * Authenticated calls to the Linkpass APIs (leap-middleware). Each one throws on a non-2xx
 * so callers can show their existing error state.
 */
async function linkpassFetch(
  path,
  { method = 'GET', body, ctx, timeoutMs, redirectOnExpiry = true } = {},
) {
  /*
   * ctx matters for the exchange pages: they call this from getServerSideProps, where
   * parseCookies() with no argument cannot see the request.
   */
  const { authToken } = parseCookies(ctx);

  /*
   * Optional because most of these are plain reads. It exists for the conversion post,
   * which waits on the partner crediting the points before it answers - with no timeout a
   * stalled partner leaves the member on a spinner for the browser default, minutes away.
   */
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  let res;
  try {
    res = await fetch(leapUrl(path), {
      method,
      headers: {
        /*
         * Omitted entirely when there is no token, not sent as "Bearer undefined".
         * /pex/me/exchanges answers a request with no Authorization header at all with 200
         * and the public partner list, but answers a malformed one with 401 - so a logged-out
         * visitor would lose the exchange page if we always sent the header.
         */
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (err) {
    // an abort is our own timeout firing, not a network fault - label it so callers can tell
    if (err.name === 'AbortError') {
      const timeoutError = new Error(`Request to ${path} timed out after ${timeoutMs}ms`);
      timeoutError.timeout = true;
      throw timeoutError;
    }
    throw err;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
  /*
   * An expired session is handled here rather than at each call site, which is where the
   * pre-Linkpass stack handled it too (handleInvalidToken on every get/post). Leaving it to
   * callers means eight of the nine tell the member the server is broken when all that
   * happened is their token aged out. The path is preserved so they come back where they were.
   *
   * redirectOnExpiry opts out where the call site has something better to say: the points
   * conversion shows an explained modal rather than dropping the member mid-transaction.
   */
  if (res.status === 401 && redirectOnExpiry) {
    if (typeof window === 'undefined') {
      const expired = new Error('Linkpass session expired');
      expired.status = 401;
      throw expired;
    }

    /*
     * A 401 straight after signing in is not an expired session - the member has a valid
     * token and leap still refuses them, which happens when their identity has no record
     * there. Sending them back to log in would land on the same 401 and loop forever, so
     * once inside the window the error is thrown for the call site to show instead.
     */
    if (recentlySentToLogin()) {
      const refused = new Error('Linkpass session was refused right after signing in');
      refused.status = 401;
      throw refused;
    }

    markSentToLogin();
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/api/auth/login?redirect=${encodeURIComponent(returnTo)}`);
    // the navigation is not instant; never resolve into a caller expecting data
    return new Promise(() => {});
  }

  if (!res.ok) {
    /*
     * The error body carries copy meant for the member, e.g. a failed points conversion
     * returns { code, status, message } where message names the partner's support line,
     * so throw an error that keeps the status and the message.
     */
    let message;
    try {
      ({ message } = await res.json());
    } catch (e) {
      message = undefined;
    }
    const error = new Error(message || `Request failed with ${res.status}`);
    error.status = res.status;
    error.serverMessage = message;
    throw error;
  }
  if (typeof window !== 'undefined') {
    clearSentToLogin();
  }

  // a 204 carries no body and res.json() would throw on the empty string
  if (res.status === 204) {
    return null;
  }
  return res.json();
}

function linkpassGet(path, ctx) {
  return linkpassFetch(path, { ctx });
}

function linkpassPost(path, body, ctx, options = {}) {
  return linkpassFetch(path, { method: 'POST', body, ctx, ...options });
}

function linkpassDelete(path, ctx) {
  return linkpassFetch(path, { method: 'DELETE', ctx });
}

export { linkpassGet, linkpassPost, linkpassDelete };
